import { Fragment, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useFacility } from '../contexts/FacilityContext';
import { useAuth } from '../contexts/AuthContext';
import ActiveFacilityBanner from '../components/ActiveFacilityBanner';
import ServiceKeyMappings from '../components/ServiceKeyMappings';
import {
  fetchCptCodePreferences, fetchCptCodeRates, fetchCptCodes, updateCptCodePreference,
} from '../services/api';
import './Admin.css';
import './CptCodeSettings.css';

// frequent = (pinned OR used) AND NOT hidden — mirrors the form's grouping.
const isFrequent = (r) => (!!r.pinned || r.uses > 0) && !r.hidden;
const effCharge  = (r) => (r.custom_charge != null ? r.custom_charge : r.default_charge);
const fmtCharge  = (c) => (c != null && c !== '' ? `$${Number(c).toFixed(2)}` : '—');

// Prices are effective-dated and append-only (sync plan Step 10): a new price starts on a date
// and closes the previous one, and the history is kept so a claim can be priced — and
// justified — by its date of service. Windows are [from, to): `to` is the day it was replaced.
const OPEN_ENDED_FROM = '2000-01-01';
const todayLocal = () => new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD
const fmtDate = (d) => (d && d !== OPEN_ENDED_FROM ? d : 'the start');

function CptCodeSettings() {
  const { selectedFacility } = useFacility();
  const { user } = useAuth();
  const facilityId = selectedFacility?.id;

  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [savingCodes, setSavingCodes] = useState(() => new Set());
  // Inline edit target: { code, field: 'description' | 'charge' }
  const [editing, setEditing]     = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editDate, setEditDate]   = useState(todayLocal());
  // Price history, one code at a time: { code, loading, error, base: [], overrides: [] }
  const [history, setHistory]     = useState(null);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const q = searchQuery.trim();
      // Empty search → the curated/used shortlist; otherwise search the full CPT list.
      const res = q
        ? await fetchCptCodes({ q, facilityId })
        : await fetchCptCodePreferences(facilityId);
      setRows((res.data || []).map((r) => ({
        ...r,
        pinned: !!r.pinned,
        hidden: !!r.hidden,
        uses: Number(r.uses) || 0,
      })));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load CPT codes');
    } finally {
      setLoading(false);
    }
  }, [facilityId, searchQuery]);

  // Debounce so typing in the search box doesn't hammer the API.
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  // Persist a full desired pref state for a row: { pinned, hidden, customDescription }, plus
  // { customCharge, effectiveFrom } ONLY when the price is what is being changed. A toggle
  // never sends a price — the server leaves an omitted price alone, and re-sending today's
  // price from a pin click would append a rate row nobody asked for.
  const persist = async (row, next) => {
    const changesPrice = Object.prototype.hasOwnProperty.call(next, 'customCharge');
    setSavingCodes((prev) => new Set(prev).add(row.code));
    setRows((prev) => prev.map((r) => (r.code === row.code
      ? { ...r, pinned: next.pinned, hidden: next.hidden, custom_description: next.customDescription }
      : r)));
    try {
      await updateCptCodePreference({ facilityId, code: row.code, ...next });
      if (changesPrice) {
        // A price may take effect on a later date, so re-read what is in force today.
        await load();
        if (history?.code === row.code) openHistory(row, { keepOpen: true });
      } else if (!searchQuery.trim() && !next.pinned && !next.hidden && !next.customDescription
          && row.custom_charge == null && row.uses === 0) {
        // In the shortlist view, a code with no pin/hide/override that's never been used is no
        // longer relevant — drop it so the list reflects what billers will see.
        setRows((prev) => prev.filter((r) => r.code !== row.code));
      }
    } catch (err) {
      setRows((prev) => prev.map((r) => (r.code === row.code
        ? { ...r, pinned: row.pinned, hidden: row.hidden, custom_description: row.custom_description }
        : r)));
      setError(err.response?.data?.error || 'Failed to save preference');
    } finally {
      setSavingCodes((prev) => { const n = new Set(prev); n.delete(row.code); return n; });
    }
  };

  // Current-state helpers so each toggle preserves the other prefs. No price here, on purpose.
  const base = (row) => ({
    pinned: row.pinned,
    hidden: row.hidden,
    customDescription: row.custom_description || null,
  });

  const openHistory = async (row, { keepOpen = false } = {}) => {
    if (!keepOpen && history?.code === row.code) { setHistory(null); return; }
    setHistory({ code: row.code, loading: true, error: null, base: [], overrides: [] });
    try {
      const res = await fetchCptCodeRates(row.code, facilityId);
      setHistory({ code: row.code, loading: false, error: null, ...res.data });
    } catch (err) {
      setHistory({ code: row.code, loading: false, error: err.response?.data?.error || 'Failed to load price history', base: [], overrides: [] });
    }
  };

  // Pin and hide are mutually exclusive — turning one on clears the other.
  const togglePin  = (row) => persist(row, { ...base(row), pinned: !row.pinned, hidden: !row.pinned ? false : row.hidden });
  const toggleHide = (row) => persist(row, { ...base(row), hidden: !row.hidden, pinned: !row.hidden ? false : row.pinned });

  const startEdit  = (row, field) => {
    setEditing({ code: row.code, field });
    setEditDate(todayLocal());
    if (field === 'description') setEditValue(row.custom_description || row.description);
    else setEditValue(effCharge(row) != null ? String(Number(effCharge(row)).toFixed(2)) : '');
  };
  const cancelEdit = () => { setEditing(null); setEditValue(''); };
  const isEditing  = (row, field) => editing?.code === row.code && editing?.field === field;

  const saveEdit = (row) => {
    if (editing?.field === 'description') {
      const v = editValue.trim();
      // Blank or unchanged-from-standard clears the override.
      const customDescription = (!v || v === row.description) ? null : v;
      persist(row, { ...base(row), customDescription });
    } else {
      const v = editValue.trim();
      let customCharge = null;
      if (v !== '') {
        const n = parseFloat(v);
        if (Number.isNaN(n) || n < 0) { setError('Price must be a non-negative number'); return; }
        // Unchanged-from-default clears the override.
        customCharge = (row.default_charge != null && n === Number(row.default_charge)) ? null : n.toFixed(2);
      }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(editDate)) { setError('Choose the date the new price takes effect'); return; }
      persist(row, { ...base(row), customCharge, effectiveFrom: editDate });
    }
    cancelEdit();
  };

  const resetDescription = (row) => persist(row, { ...base(row), customDescription: null });
  // Clearing an override is itself a dated change: from today, the standard price applies.
  const resetCharge      = (row) => persist(row, { ...base(row), customCharge: null, effectiveFrom: todayLocal() });

  if (!selectedFacility) {
    return (
      <div className="admin-page">
        <div className="admin-header">
          <div className="admin-header-text">
            <h1>CPT / HCPCS Shortlist</h1>
            <p>Curate the codes and prices that auto-fill when generating claims</p>
          </div>
        </div>
        <div className="admin-empty">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          <h3>No facility selected</h3>
          <p>Select a facility to manage its CPT/HCPCS shortlist.</p>
          <Link to="/" className="admin-create-btn" style={{ marginTop: 12, textDecoration: 'none' }}>Select a Facility</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <ActiveFacilityBanner />

      <div className="admin-header">
        <div className="admin-header-text">
          <h1>CPT / HCPCS Shortlist</h1>
          <p>
            Pin codes to always show them in <strong>Frequently used</strong> on the claim forms, or hide ones you
            never bill. Override the description or price for this facility. Codes you use are surfaced automatically.
          </p>
        </div>
      </div>

      <div className="admin-toolbar">
        <div className="admin-search-wrapper">
          <svg className="admin-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="admin-search"
            placeholder="Search all CPT/HCPCS codes to pin, hide, or price..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="admin-search-clear" onClick={() => setSearchQuery('')} aria-label="Clear search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        <span className="admin-count">
          {searchQuery.trim() ? `${rows.length} matching` : `${rows.length} in shortlist`}
        </span>
      </div>

      {error && <div className="admin-modal-alert error" style={{ marginBottom: 14 }}><span>{error}</span></div>}

      <div className="admin-table-card">
        {loading ? (
          <div className="admin-empty"><h3>Loading…</h3></div>
        ) : rows.length === 0 ? (
          <div className="admin-empty">
            <h3>{searchQuery.trim() ? 'No CPT codes match your search' : 'Nothing in the shortlist yet'}</h3>
            <p>
              {searchQuery.trim()
                ? 'Try a different code or description.'
                : 'Codes appear here automatically as you bill them, or search above to pin codes you want to keep handy.'}
            </p>
          </div>
        ) : (
          <table className="admin-table rcs-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
                <th className="ccs-price-col">Price</th>
                <th className="rcs-uses-col">Used</th>
                <th className="rcs-status-col">Status</th>
                <th className="rcs-actions-col">Shortlist</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const saving = savingCodes.has(row.code);
                return (
                  <Fragment key={row.code}>
                  <tr className={row.hidden ? 'rcs-row-hidden' : ''}>
                    <td><span className="rcs-code">{row.code}</span></td>
                    <td>
                      {isEditing(row, 'description') ? (
                        <div className="rcs-desc-edit">
                          <input
                            className="rcs-desc-input"
                            type="text"
                            value={editValue}
                            maxLength={150}
                            autoFocus
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(row); if (e.key === 'Escape') cancelEdit(); }}
                          />
                          <button className="rcs-desc-save" onClick={() => saveEdit(row)} disabled={saving}>Save</button>
                          <button className="rcs-desc-cancel" onClick={cancelEdit}>Cancel</button>
                        </div>
                      ) : (
                        <div className="rcs-desc-view">
                          <span>{row.custom_description || row.description}</span>
                          {row.custom_description && <span className="rcs-pill rcs-pill-custom">Custom</span>}
                          <button className="rcs-desc-edit-btn" onClick={() => startEdit(row, 'description')} disabled={saving} title="Edit description for this facility">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                          {row.custom_description && (
                            <button className="rcs-desc-reset-btn" onClick={() => resetDescription(row)} disabled={saving} title="Reset to standard description">Reset</button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="ccs-price-col">
                      {isEditing(row, 'charge') ? (
                        <div className="ccs-price-edit">
                          <input
                            className="ccs-price-input"
                            type="number"
                            step="0.01"
                            min="0"
                            value={editValue}
                            autoFocus
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(row); if (e.key === 'Escape') cancelEdit(); }}
                          />
                          <label className="ccs-effective">
                            <span>from</span>
                            <input
                              className="ccs-effective-input"
                              type="date"
                              value={editDate}
                              onChange={(e) => setEditDate(e.target.value)}
                              title="The date this price takes effect. Earlier dates of service keep the price they had."
                            />
                          </label>
                          <button className="rcs-desc-save" onClick={() => saveEdit(row)} disabled={saving}>Save</button>
                          <button className="rcs-desc-cancel" onClick={cancelEdit}>Cancel</button>
                        </div>
                      ) : (
                        <div className="ccs-price-view">
                          <span className="ccs-price">{fmtCharge(effCharge(row))}</span>
                          {row.custom_charge != null && <span className="rcs-pill rcs-pill-custom">Custom</span>}
                          {row.custom_charge != null && row.override_effective_from && row.override_effective_from !== OPEN_ENDED_FROM && (
                            <span className="ccs-since">since {row.override_effective_from}</span>
                          )}
                          <button
                            className={`ccs-history-btn ${history?.code === row.code ? 'active' : ''}`}
                            onClick={() => openHistory(row)}
                            title="Price history for this code"
                          >
                            History
                          </button>
                          <button className="rcs-desc-edit-btn" onClick={() => startEdit(row, 'charge')} disabled={saving} title="Edit price for this facility">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                          {row.custom_charge != null && (
                            <button className="rcs-desc-reset-btn" onClick={() => resetCharge(row)} disabled={saving} title="Reset to standard price">Reset</button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="rcs-uses-col">
                      {row.uses > 0 ? <span className="rcs-uses-badge">{row.uses}×</span> : <span className="rcs-muted">—</span>}
                    </td>
                    <td className="rcs-status-col">
                      {row.hidden ? (
                        <span className="rcs-pill rcs-pill-hidden">Hidden</span>
                      ) : isFrequent(row) ? (
                        <span className="rcs-pill rcs-pill-frequent">Frequently used</span>
                      ) : (
                        <span className="rcs-muted">—</span>
                      )}
                    </td>
                    <td className="rcs-actions-col">
                      <div className="rcs-actions">
                        <button
                          className={`admin-action-btn rcs-pin-btn ${row.pinned ? 'active' : ''}`}
                          onClick={() => togglePin(row)}
                          disabled={saving}
                          title={row.pinned ? 'Unpin from Frequently used' : 'Pin to Frequently used'}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill={row.pinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 4V2H7v2h1v6l-2 2v2h5v6h2v-6h5v-2l-2-2V4z" />
                          </svg>
                          {row.pinned ? 'Pinned' : 'Pin'}
                        </button>
                        <button
                          className={`admin-action-btn rcs-hide-btn ${row.hidden ? 'active' : ''}`}
                          onClick={() => toggleHide(row)}
                          disabled={saving}
                          title={row.hidden ? 'Unhide' : 'Hide from Frequently used'}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            {row.hidden ? (
                              <>
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                <line x1="1" y1="1" x2="23" y2="23" />
                              </>
                            ) : (
                              <>
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                              </>
                            )}
                          </svg>
                          {row.hidden ? 'Hidden' : 'Hide'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {history?.code === row.code && (
                    <tr className="ccs-history-row">
                      <td colSpan={6}>
                        <PriceHistory history={history} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* 2026-10-13 (A5) — admins map a source's charge-line keys to the code coders should consider. */}
      {user?.role === 'admin' && <ServiceKeyMappings facilityId={facilityId} />}
    </div>
  );
}

// The price windows on file for one code, newest first. The base schedule and this facility's
// override each have their own timeline; an override row with no amount means "back to the
// standard price from that date".
function PriceHistory({ history }) {
  if (history.loading) return <div className="ccs-history-empty">Loading price history…</div>;
  if (history.error) return <div className="ccs-history-empty">{history.error}</div>;

  const range = (r) => `${fmtDate(r.effective_from)} → ${r.effective_to ? r.effective_to : 'now'}`;
  const upcoming = (r) => r.effective_from > todayLocal();

  return (
    <div className="ccs-history">
      <div className="ccs-history-col">
        <h4>This facility's price</h4>
        {history.overrides.length === 0 ? (
          <div className="ccs-history-empty">No facility price — the standard price applies.</div>
        ) : (
          <ul>
            {history.overrides.map((r) => (
              <li key={r.effective_from}>
                <span className="ccs-history-window">{range(r)}</span>
                <span className="ccs-price">{r.custom_charge != null ? fmtCharge(r.custom_charge) : 'Standard price'}</span>
                {r.custom_technical_charge != null && <span className="ccs-history-comp">TC {fmtCharge(r.custom_technical_charge)}</span>}
                {r.custom_professional_charge != null && <span className="ccs-history-comp">26 {fmtCharge(r.custom_professional_charge)}</span>}
                {upcoming(r) && <span className="rcs-pill rcs-pill-custom">Scheduled</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="ccs-history-col">
        <h4>Standard price</h4>
        {history.base.length === 0 ? (
          <div className="ccs-history-empty">No standard price on file.</div>
        ) : (
          <ul>
            {history.base.map((r) => (
              <li key={r.effective_from}>
                <span className="ccs-history-window">{range(r)}</span>
                <span className="ccs-price">{fmtCharge(r.default_charge)}</span>
                {r.technical_charge != null && <span className="ccs-history-comp">TC {fmtCharge(r.technical_charge)}</span>}
                {r.professional_charge != null && <span className="ccs-history-comp">26 {fmtCharge(r.professional_charge)}</span>}
                {upcoming(r) && <span className="rcs-pill rcs-pill-custom">Scheduled</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default CptCodeSettings;
